import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAuthStore } from '../../stores/authStore';
import { useUIStore } from '../../stores/uiStore';
import {
  LayoutDashboard,
  Building2,
  AppWindow,
  LogOut,
  User,
  Users,
  ClipboardList,
  Menu,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useEffect } from 'react';
import { ThemeToggleCompact } from '../common/theme-toggle';

export function DashboardLayout() {
  const { user, logout, isSuperAdmin, isAdmin } = useAuth();
  const { clearError } = useAuthStore();
  const {
    sidebarCollapsed,
    sidebarOpen,
    isMobile,
    toggleSidebar,
    setSidebarOpen,
    setIsMobile
  } = useUIStore();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    clearError();
    navigate('/login');
  };

  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [setIsMobile, setSidebarOpen]);

  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile, setSidebarOpen]);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, allowed: true, gradient: 'from-primary-500 to-secondary-500' },
    { name: 'Organizations', href: '/dashboard/orgs', icon: Building2, allowed: isSuperAdmin(), gradient: 'from-secondary-500 to-accent-500' },
    { name: 'Applications', href: '/dashboard/apps', icon: AppWindow, allowed: isAdmin(), gradient: 'from-accent-500 to-primary-500' },
    { name: 'Users', href: '/dashboard/users', icon: Users, allowed: isSuperAdmin(), gradient: 'from-success-500 to-primary-500' },
    { name: 'Audit Logs', href: '/dashboard/audit', icon: ClipboardList, allowed: isSuperAdmin(), gradient: 'from-warning-500 to-danger-500' },
    { name: 'Profile', href: '/dashboard/profile', icon: User, allowed: true, gradient: 'from-primary-500 to-accent-500' },
  ];

  useEffect(() => {
    clearError();
  }, [location.pathname]);

  const getSidebarWidth = () => {
    if (isMobile) return sidebarOpen ? 'w-64' : 'w-0';
    return sidebarCollapsed ? 'w-20' : 'w-64';
  };

  return (
    <div className="flex h-screen bg-background dark:bg-background-dark overflow-hidden">
      {isMobile && sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
      )}
      <aside className={`${getSidebarWidth()} ${sidebarOpen || !isMobile ? 'block' : 'hidden'} fixed md:relative z-50 h-screen bg-surface dark:bg-surface-dark border-r border-neutral-200 dark:border-gray-800 flex flex-col shadow-lg transition-all duration-300 overflow-hidden`}>
        {/* Header with collapse button on right edge */}
        <div className="bg-gradient-to-r from-primary-500 via-secondary-500 to-accent-500 p-4 text-white flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 overflow-hidden flex-1">
            <div className="bg-white/20 backdrop-blur-md rounded shadow-inner flex-shrink-0">
              <img src="/logo.png" alt="Logo" className="h-6 w-auto object-contain" />
            </div>
            {!sidebarCollapsed && <span className="font-bold text-sm tracking-tight whitespace-nowrap">AuthKeystone</span>}
          </div>
          {!isMobile && (
            <button onClick={toggleSidebar} className="hover:bg-white/20 rounded transition-colors" title={sidebarCollapsed ? 'Expand' : 'Collapse'}>
              {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </button>
          )}
          {isMobile && (
            <button onClick={() => setSidebarOpen(false)} className="hover:bg-white/20 rounded transition-colors">
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Role badge */}
        {!sidebarCollapsed && (
          <div className="flex items-center gap-1.5 px-2 bg-white/10 backdrop-blur-md rounded text-[10px] font-medium border border-white/20 mt-2 mx-4">
            <span className="w-1.5 h-1.5 rounded-full bg-success-400 animate-pulse"></span>
            {user?.role === 'super_admin' ? '👑 Super Admin' : user?.role === 'admin' ? '🛡️ Org Admin' : '👤 User'}
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-1 bg-surfaceAlt dark:bg-surfaceAlt-dark">
          {navigation.filter(item => item.allowed).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link key={item.name} to={item.href} className={`group flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive ? `bg-gradient-to-r ${item.gradient} text-white shadow-md shadow-primary-500/25 transform scale-[1.02]` : 'text-neutral-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 hover:text-primary-700 dark:hover:text-primary-200 hover:shadow-md border border-transparent'} ${sidebarCollapsed ? 'justify-center' : ''}`} title={sidebarCollapsed ? item.name : ''}>
                <div className={`rounded transition-colors ${isActive ? 'bg-white/20' : 'bg-neutral-200 dark:bg-gray-700 group-hover:bg-primary-200 dark:group-hover:bg-primary-800 group-hover:text-primary-700'}`}>
                  <Icon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-neutral-600 dark:text-gray-400 group-hover:text-primary-700'}`} />
                </div>
                {!sidebarCollapsed && (
                  <>
                    <span className="flex-1">{item.name}</span>
                    {isActive && <div className="w-1 h-4 bg-white rounded-full animate-pulse shadow-sm"></div>}
                  </>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Footer with logout and theme toggle */}
        <div className="p-2 border-t border-neutral-200 dark:border-gray-800 bg-surfaceAlt dark:bg-surfaceAlt-dark">
          {!sidebarCollapsed && (
            <div className="mb-2 px-2">
              <p className="text-[10px] font-bold text-gray-900 dark:text-gray-100 truncate">{user?.name || 'User'}</p>
              <p className="text-[9px] text-neutral-500 dark:text-gray-400 truncate">{user?.email}</p>
            </div>
          )}
          <div className={`flex ${sidebarCollapsed ? 'flex-col gap-1' : 'gap-2'}`}>
            <button onClick={handleLogout} className={`btn-danger btn-ripple flex items-center justify-center gap-2 text-xs font-semibold py-2 ${sidebarCollapsed ? 'px-1 w-full' : 'flex-1'}`} title={sidebarCollapsed ? 'Sign Out' : ''}>
              <LogOut className="h-3.5 w-3.5" />
              {!sidebarCollapsed && 'Sign Out'}
            </button>
            <div className={`${sidebarCollapsed ? 'w-full' : ''} flex ${sidebarCollapsed ? 'justify-center' : ''}`}>
              <ThemeToggleCompact />
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto bg-background dark:bg-background-dark transition-all duration-300 w-full">
        {isMobile && (
          <div className="sticky top-0 z-30 bg-surface/95 dark:bg-surface-dark/95 backdrop-blur-md border-b border-neutral-200 dark:border-gray-800 shadow-sm">
            <div className="flex items-center justify-between p-3">
              <div className="flex items-center gap-3">
                <button onClick={toggleSidebar} className="p-2 hover:bg-neutral-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
                  <Menu className="h-6 w-6 dark:text-gray-200" />
                </button>
                <div className="flex items-center gap-2">
                  <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded">
                    <img src="/logo.png" alt="Logo" className="h-5 w-auto object-contain" />
                  </div>
                  <span className="font-bold text-sm dark:text-gray-100">AuthKeystone</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-primary-600 bg-primary-50 dark:bg-primary-900/30 dark:text-primary-300 px-2 py-1 rounded-full">
                  {user?.role === 'super_admin' ? '👑' : user?.role === 'admin' ? '🛡️' : '👤'}
                </span>
                <ThemeToggleCompact />
              </div>
            </div>
          </div>
        )}
        <div className="max-w-7xl mx-auto p-3 md:p-4 dark:text-gray-100">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
